import { RESPONSE_EXPORT_SERVER_REDACTION_POLICY } from '@/contracts/responses'
import type {
  ListResponseRecordsParams,
  ListResponseRecordsResult,
  ResponseDetailContract,
  ResponseExportDataSource,
  ResponseExportDetailContract,
  ResponseExportFormat,
  ResponseExportServerDeleteResultContract,
  ResponseExportServerFormat,
  ResponseExportServerJobRequest,
  ResponseExportServerJobStatus,
  ResponseExportServerSafeJobContract,
} from '@/contracts/responses'
import {
  getMockResponseDetail,
  listMockResponseOwners,
  listMockResponseRecords,
} from '@/lib/mock-api/responses'
import {
  DataClientRequestError,
  apiOrMock,
  buildQuery,
  metaWithSource,
  requestApiEnvelope,
  toSourceMeta,
} from './http'

export type ResponseExportServerDownloadedFile = {
  format: ResponseExportServerFormat
  fileName: string
  mimeType: string
  content: string
  rowCount: number
  source: 'd1_worker_stream' | 'd1_r2_export'
  mockSafe: false
}

export async function listResponseRecords(
  params: ListResponseRecordsParams = {},
): Promise<ListResponseRecordsResult> {
  return apiOrMock({
    label: 'listResponseRecords',
    api: async () => {
      const formId = params.formId
      if (!formId) return listMockResponseRecords(params)
      const envelope = await requestApiEnvelope<ListResponseRecordsResult['data']>(
        `/api/forms/${formId}/responses${buildQuery({ ...params, formId: undefined })}`,
      )
      return {
        data: envelope.data,
        meta: metaWithSource(envelope.meta),
      }
    },
    mock: () => listMockResponseRecords(params),
  })
}

export async function getResponseDetail(responseId: string, formId?: string) {
  return apiOrMock({
    label: 'getResponseDetail',
    api: async (): Promise<ResponseDetailContract> => {
      if (!formId) return getMockResponseDetail(responseId)
      const envelope = await requestApiEnvelope<ResponseDetailContract>(
        `/api/forms/${formId}/responses/${responseId}`,
      )
      return {
        ...envelope.data,
        sourceMeta: toSourceMeta(envelope.meta ?? envelope.data.sourceMeta),
      }
    },
    mock: () => getMockResponseDetail(responseId),
  })
}

export async function getResponseExportDetails(
  formId: string,
  params: {
    format?: Extract<ResponseExportFormat, 'csv' | 'json'>
    dataSource?: ResponseExportDataSource
    limit?: number
    page?: number
    filingDateFrom?: string
    filingDateTo?: string
  } = {},
): Promise<ResponseExportDetailContract> {
  const envelope = await requestApiEnvelope<ResponseExportDetailContract>(
    `/api/forms/${formId}/responses/export${buildQuery({
      format: params.format ?? 'csv',
      dataSource: params.dataSource,
      limit: params.limit ?? 100,
      page: params.page,
      filingDateFrom: params.filingDateFrom,
      filingDateTo: params.filingDateTo,
    })}`,
  )
  return envelope.data
}

export async function queueResponseExportJob(
  formId: string,
  request: ResponseExportServerJobRequest,
): Promise<ResponseExportServerSafeJobContract> {
  const envelope = await requestApiEnvelope<unknown>(`/api/forms/${formId}/responses/export-jobs`, {
    method: 'POST',
    body: JSON.stringify(request),
  })
  return parseResponseExportServerJob(envelope.data)
}

export async function processResponseExportJob(
  formId: string,
  jobId: string,
): Promise<ResponseExportServerSafeJobContract> {
  const envelope = await requestApiEnvelope<unknown>(
    `/api/forms/${formId}/responses/export-jobs/${jobId}/process-now`,
    {
      method: 'POST',
    },
  )
  return parseResponseExportServerJob(envelope.data)
}

export async function downloadResponseExportJobFile(
  formId: string,
  jobId: string,
): Promise<ResponseExportServerDownloadedFile> {
  const response = await fetch(`/api/forms/${formId}/responses/export-jobs/${jobId}/download`, {
    credentials: 'include',
    headers: new Headers(),
  })

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as
      | { error?: string; code?: string; details?: unknown }
      | null
    throw new DataClientRequestError(
      response.status,
      body?.error ?? `API request failed: ${response.status}`,
      body?.code,
      body?.details,
    )
  }

  const content = await response.text()
  const mimeType = response.headers.get('Content-Type') ?? 'text/csv; charset=utf-8'
  const format = inferResponseExportDownloadedFormat(mimeType)
  const storage = response.headers.get('X-MiaoForm-Export-Storage')
  const fileName =
    parseAttachmentFileName(response.headers.get('Content-Disposition')) ??
    `miaoform-responses.${format}`

  return {
    format,
    fileName,
    mimeType,
    content,
    rowCount: inferResponseExportDownloadedRowCount(content, format),
    source: storage === 'r2' ? 'd1_r2_export' : 'd1_worker_stream',
    mockSafe: false,
  }
}

export async function deleteResponseExportJob(
  formId: string,
  jobId: string,
): Promise<ResponseExportServerDeleteResultContract> {
  const envelope = await requestApiEnvelope<ResponseExportServerDeleteResultContract>(
    `/api/forms/${formId}/responses/export-jobs/${jobId}`,
    {
      method: 'DELETE',
    },
  )
  return envelope.data
}

export async function listResponseOwners(formId?: string): Promise<string[]> {
  return apiOrMock({
    label: 'listResponseOwners',
    api: async () => {
      const records = await listResponseRecords({ formId, limit: 100 })
      return Array.from(new Set(records.data.map((record) => record.ownerName))).sort(
        (left, right) => left.localeCompare(right, 'zh-Hant'),
      )
    },
    mock: () => listMockResponseOwners(formId),
  })
}

function parseResponseExportServerJob(value: unknown): ResponseExportServerSafeJobContract {
  const record = asRecord(value)
  return {
    exportJobId: readString(record.exportJobId, ''),
    status: parseResponseExportJobStatus(record.status),
    format: parseResponseExportFormat(record.format),
    fileName: readString(record.fileName, 'miaoform-responses.csv'),
    rowCount: readNumber(record.rowCount),
    rowLimit: readNumber(record.rowLimit, 1000),
    chunkSize: readNumber(record.chunkSize, 1000),
    partCount: readNumber(record.partCount, 1),
    downloadMode: record.downloadMode === 'signed_url' ? 'signed_url' : 'worker_stream',
    downloadUrlExpiresAt:
      typeof record.downloadUrlExpiresAt === 'string' ? record.downloadUrlExpiresAt : undefined,
    includeSensitiveFields: Boolean(record.includeSensitiveFields),
    redactionPolicy: RESPONSE_EXPORT_SERVER_REDACTION_POLICY,
    createdAt: typeof record.createdAt === 'string' ? record.createdAt : undefined,
    expiresAt: typeof record.expiresAt === 'string' ? record.expiresAt : undefined,
    safeErrorCode: typeof record.safeErrorCode === 'string' ? record.safeErrorCode : undefined,
    retryable: typeof record.retryable === 'boolean' ? record.retryable : undefined,
  }
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : {}
}

function readString(value: unknown, fallback: string) {
  return typeof value === 'string' && value.trim() ? value : fallback
}

function readNumber(value: unknown, fallback = 0) {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback
}

function parseResponseExportFormat(value: unknown): ResponseExportServerFormat {
  return value === 'json' ? 'json' : 'csv'
}

function parseResponseExportJobStatus(value: unknown): ResponseExportServerJobStatus {
  if (
    value === 'queued' ||
    value === 'processing' ||
    value === 'ready' ||
    value === 'failed' ||
    value === 'expired'
  ) {
    return value
  }
  return 'queued'
}

function inferResponseExportDownloadedFormat(mimeType: string): ResponseExportServerFormat {
  return mimeType.toLowerCase().includes('json') ? 'json' : 'csv'
}

function parseAttachmentFileName(disposition: string | null) {
  if (!disposition) return null
  const encodedMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i)
  if (encodedMatch?.[1]) return decodeURIComponent(encodedMatch[1].trim())
  const quotedMatch = disposition.match(/filename="([^"]+)"/i)
  if (quotedMatch?.[1]) return quotedMatch[1].trim()
  const plainMatch = disposition.match(/filename=([^;]+)/i)
  return plainMatch?.[1]?.trim() ?? null
}

function inferResponseExportDownloadedRowCount(
  content: string,
  format: ResponseExportServerDownloadedFile['format'],
) {
  if (format === 'json') {
    const parsed = safeJsonParse(content)
    if (typeof parsed?.rowCount === 'number' && Number.isFinite(parsed.rowCount)) {
      return parsed.rowCount
    }
    if (Array.isArray(parsed?.rows)) return parsed.rows.length
    return 0
  }
  const lines = content.split(/\r?\n/).filter((line) => line.trim().length > 0)
  return Math.max(lines.length - 1, 0)
}

function safeJsonParse(content: string): { rowCount?: number; rows?: unknown[] } | null {
  try {
    const parsed = JSON.parse(content)
    return parsed && typeof parsed === 'object' ? parsed : null
  } catch {
    return null
  }
}
